<template>
  <div class="row stashboard-settings">
    <div v-if="error" class="callout alert">{{ error }}</div>

    <h1 class="page-header text-center">stashboard settings</h1>

    <sb-loading-icon v-if="loading" />

    <div v-else class="columns large-12" @click="toggleStaticHeaderBlock = !toggleStaticHeaderBlock">
      <div class="sb-row start align-end">
        <fai v-if="toggleStaticHeaderBlock" :icon="['fal', 'chevron-up']" class="blue-icon" />
        <fai v-else :icon="['fal', 'chevron-down']" class="blue-icon" />
        <h3 class="label-row" style="padding-left: 10px;">static header</h3>
      </div>
    </div>

    <transition name="fade">
      <div v-if="toggleStaticHeaderBlock" class="columns large-12" :class="{'border-bottom': toggleStaticHeaderBlock}">
        <div class="row small-collapse py-10">
          <div class="columns small-7">
            <div class="row small-collapse py-10">
              <div class="columns small-6 small-offset-1">
                <span class="label-row">url</span>
              </div>
              <div class="columns small-4">
                <span class="label-row">icon</span>
              </div>
            </div>

            <div class="sb-row py-10" v-for="(stashboardHeader, index) in stashboardHeadersList" :key="index">
              <div class="columns small-1 text-center">
                <fai :icon="['far', 'chevron-up']"
                    class="chevron"
                    :class="{ disabled: index === 0 }"
                    @click="moveLinkUp(index)" />
                <span class="position">{{ index + 1 }}</span>
                <fai :icon="['far', 'chevron-down']"
                    class="chevron"
                    :class="{ disabled: index === (stashboardHeadersList.length - 1)}"
                    @click="moveLinkDown(index)" />
              </div>
              <div class="columns small-6 break-all">
                <span>{{ stashboardHeader.url }}</span>
              </div>
              <div class="columns small-4">
                <img v-if="stashboardHeader.attachment" :src="cleanIconUrl(stashboardHeader.attachment.url)" class="stashboard-icon" title="stashboard image"/>
              </div>
              <div class="columns small-1">
                <a class="delete" @click="clickedRemove(stashboardHeader.id, index)" data-cy="stashboard-headers-remove-button">&times;</a>
              </div>
            </div>
          </div>

          <div class="columns small-4 small-offset-1 text-center">
            <div class="row small-collapse py-10">
              <span class="label-row">preview</span>
            </div>
            <div class="preview row py-10">
              <main class="iphone mx-auto mt-1.5">
                <!-- This is only for building the iphone -->
                <section class="speaker absolute top-0 left-1/2 z-1" />

                <!-- This is only for building the iphone -->
                <section class="camera absolute top-0 z-1" />

                <section class="content w-full block pt-6 pb-3 font-primary text-black">
                  <div class="flex flex-row flex-wrap justify-between">
                    <div class="text-md pl-1.5">
                      {{ currentMerchant.display_name }}
                    </div>
                    <div class="flex flex-row justify-start pr-1.5">
                      <template v-for="(stashboardHeader, index) in stashboardHeadersList">
                        <img v-if="stashboardHeader.attachment" :key="index" :src="cleanIconUrl(stashboardHeader.attachment.url)" class="w-6 h-6 ml-1.5" title="stashboard image"/>
                      </template>
                    </div>
                  </div>
                </section>
              </main>
            </div>
          </div>
        </div>
        <div class="row py-20">
          <div class="columns small-10 add-link-form">
            <template v-if="!isStashboardHeadersGreaterThanOrEqualTo5">
              <a v-if="!showNewStashboardHeaderForm"
                class="add-link"
                @click="showNewStashboardHeaderForm = true"
                data-cy="stashboard-headers-add-new-static-url-button"
              >add new static url</a>

              <div v-else class="row">
                <div class="columns small-6 relative">
                  <input type="text"
                        v-model="url"
                        placeholder="url"
                        maxlength="255"
                        :class="{error: isInvalidUrl(url)}"
                        data-cy="stashboard-headers-thumbnail-url-input"
                  />
                  <span class="notice">include http:// or https://</span>
                </div>

                <div class="columns small-1 text-center">
                  <template v-if="!loadingIcon">
                    <img v-if="icon" :src="icon.url" class="stashboard-icon-upload" alt="icon" />
                    <a v-else v-file-upload:png,gif,jpeg="(file, error) => { processIcon(file, error)}" class="button icon-button" data-cy="stashboard-headers-thumbnail-image-upload-button">
                      <fai :icon="['fas', 'cloud-upload-alt']" />
                    </a>
                  </template>
                  <sb-loading-icon v-if="loadingIcon" />
                </div>

                <div class="columns small-1 text-center">
                  <button class="sb-button" :class="{ disabled: isAddButtonDisabled }" @click="clickedAdd">add</button>
                </div>

                <div class="columns small-1 text-center">
                  <button class="sb-button clear cancel" @click="clickedCancel">cancel</button>
                </div>
              </div>
            </template>
            <template v-else>
              <a class="disabled">max limit reached</a>
            </template>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
  import { stashboardAPI } from "../../services/api/stashboardAPI";
  import { mapState } from "vuex";
  import { arrayMoveUtil, isUrlFormatValidUtil } from "../../utilities";

  const genericErrorMessage = "error, please refresh or contact support";

  export default {
    name: "StashboardSettings",

    data() {
      return {
        error: null,
        icon: null,
        loading: false,
        loadingIcon: false,
        showNewStashboardHeaderForm: false,
        stashboardHeaders: [],
        toggleStaticHeaderBlock: false,
        url: null
      }
    },

    async created() {
      this.loading = true;
      this.$store.dispatch('LOAD_CURRENT_MERCHANT');
      this.stashboardHeaders = await stashboardAPI.getStashboardHeaders().finally(() => this.loading = false);
    },

    computed: {
      stashboardHeadersList() {
        return this.stashboardHeaders;
      },

      isStashboardHeadersGreaterThanOrEqualTo5(){
        return this.stashboardHeaders.length >= 5;
      },

      isAddButtonDisabled() {
        return this.isInvalidUrl(this.url) || !this.icon;
      },

      ...mapState(["currentMerchant"])
    },

    methods: {
      clearFormFields() {
        this.url = null;
        this.icon = null;
        return
      },

      cleanIconUrl(url) {
        if (url.startsWith("http")) {
          return `${url}?h=47&w=47`;
        }
        return url;
      },

      async clickedAdd() {
        try {
          this.cleanErrors();
          const newStashboardHeader = {
            url: this.url,
            attachment: this.icon
          };
          this.showNewStashboardHeaderForm = false;
          const { stashboard_header_image } = await stashboardAPI.postStashboardHeader(newStashboardHeader);
          this.stashboardHeaders.push(stashboard_header_image);
          this.clearFormFields();
        } catch(e) {
          this.error = genericErrorMessage;
        }
      },

      clickedCancel() {
        this.cleanErrors();
        this.clearFormFields();
        this.showNewStashboardHeaderForm = false;
        return
      },

      async clickedRemove(stashboardHeaderID, index) {
        try {
          await stashboardAPI.deleteStashboardHeader(stashboardHeaderID);
          this.stashboardHeaders.splice(index, 1);
        } catch(e) {
          this.error = genericErrorMessage;
        }
      },

      cleanErrors(){
        if(this.error){
          this.error = null;
        }
      },

      isInvalidUrl(url) {
        return !url || !isUrlFormatValidUtil(url);
      },

      async moveLinkDown(index) {
        try {
          await stashboardAPI.patchStashboardHeaderIncreasePriority(this.stashboardHeaders[index].id);
          this.stashboardHeaders = arrayMoveUtil(this.stashboardHeaders, index, index + 1);
        } catch(e) {
          this.error = genericErrorMessage;
        }
      },

      async moveLinkUp(index) {
        try {
          await stashboardAPI.patchStashboardHeaderDecreasePriority(this.stashboardHeaders[index].id);
          this.stashboardHeaders = arrayMoveUtil(this.stashboardHeaders, index, index - 1);
        } catch(e) {
          this.error = genericErrorMessage;
        }
      },

      processIcon(file, error){
        this.cleanErrors();
        this.loadingIcon = true;
        if(!error) {
          const reader = new FileReader();
          reader.onload = event => {
            file.url = event.target.result;
            this.icon = file;
          }
          reader.readAsDataURL(file);
        } else {
          this.error = error;
        }
        this.loadingIcon = false;
      }
    }
  }
</script>

<style lang="scss" scoped>
.stashboard-settings {
  h3 {
    cursor: pointer;
    margin: 0;
  }

  .align-end {
    align-items: end;
  }

  .relative {
    position: relative;
  }

  .notice {
    color: gray;
    font-size: 13px;
    left: 25px;
    position: absolute;
    top: 40px;
  }

  .sb-row .columns {
    padding: 0;
  }

  .add-link-form {
    padding-left: 43px;
  }

  .py-10{
    padding: 10px 0;
  }

  .py-20{
    padding: 20px 0;
  }

  .label-row {
    font-family: "AileronLight";
  }

  .blue-icon {
    color: #128ff9;
    height: 42px;
    width: 42px;
  }

  .static-url-dropdown {
    margin: 0;
  }

  .static-url-dropdown >>> .label-container{
    background-color: white;
    color: #128ff9;
  }

  .error {
    border-color: #f91253;
    color: #f91253;
  }

  .preview {
    height: 75px;
  }

  .columns.small-1 {
    padding: 0 5px;
  }

  .row .break-all {
    word-break: break-all;
    padding-right: 15px;
  }

  .fade-enter-active,
  .fade-leave-active {
    transition: opacity .5s;
  }

  .fade-enter,
  .fade-leave-to {
    opacity: 0;
  }

  .border-bottom {
    border-bottom: 1px solid #128ff9;
  }

  input {
    margin: 0;
  }

  .delete {
    font-size: 20px;
  }

  .delete,
  .cancel {
    color: #a4a4a4;
  }

  .delete:hover,
  .cancel:hover {
    color: #f91253;
  }

  .clear {
    background-color: transparent;
  }

  .icon-button {
    margin: .15em;
    padding: .55em .65em;
  }

  .sb-button {
    padding: .6rem 1rem;
    font-size: 1rem;
    font-family: "AileronThin";
  }

  .stashboard-icon {
    width: 47px;
    height: 47px;
  }

  .stashboard-icon-upload {
    width: 38px;
    height: 38px;
  }

  .stashboard-icon-preview {
    width: 25px;
    height: 25px;
    margin: 0 3px;
  }

  .add-link {
    line-height: 39px;
  }

  .disabled {
    opacity: 0.5;
    pointer-events: none;
  }

  .position {
    font-size: 0.83em;
  }

  .chevron {
    color: #128ff9;
    cursor: pointer;
    display: block;
    margin: 0 auto;
  }

  .sb-button {
    padding: .2rem .9rem;
  }

  .sb-button.clear {
    border: none;
  }

  .iphone {
    background-color: #fff;
    border-radius: 30px 30px 0 0;
    box-shadow: 0px 16px 0px 0px #ffffff,
                0px 0px 0px 6px #1f1f1f,
                -5px 0px 5px 3px rgb(255, 255, 255, .45),
                5px 0px 5px 3px rgb(255, 255, 255, .45),
                0px 0px 0px 15px #111111;
    height: 65px;
    position: relative;
    width: 250px;

    .z-1 {
      z-index: 1;
    }

    &:before {
      background-color: rgb(31, 31, 31);
      border-radius: 0px 0px 15px 15px;
      content: "";
      height: 20px;
      left: 50%;
      position: absolute;
      top: 0px;
      transform: translateX(-50%);
      width: 60%;
      z-index: 1;
    }

    .speaker {
      background-color: #101010;
      border-radius: 8px;
      box-shadow: inset 0px -3px 3px 0px rgba(256, 256, 256, 0.2);
      height: 4px;
      transform: translate(-50%, 6px);
      width: 20%;
    }

    .camera {
      background-color: #101010;
      border-radius: 8px;
      box-shadow: inset 0px -3px 2px 0px rgba(256, 256, 256, 0.2);
      height: 8px;
      left: -7%;
      transform: translate(180px, 4px);
      width: 8px;

      &:after {
        background-color: #2d4d76;
        border-radius: 4px;
        box-shadow: inset 0px -2px 2px rgba(0, 0, 0, 0.5);
        content: '';
        display: block;
        height: 6px;
        left: 1px;
        position: absolute;
        top: 2px;
        width: 6px;
      }
    }

    .content {
      overflow-y: scroll;
      /* This is used to hide the vertical scroll bar */
      -ms-overflow-style: none;
      scrollbar-width: none;

      &::-webkit-scrollbar {
        display: none;
      }
    }

    .power-button {
      background-color: #101010;
      border-radius: 8px;
      box-shadow: inset -3px 0px 1px -1px rgba(255, 255, 255, .2);
      height: 50px;
      right: -19px;
      transform: translate(-50%, 0);
      width: 4px;
    }
  }
}
</style>

